"use client";

import { Button } from "@shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/components/ui/dialog";
import { toast } from "@shared/components/ui/snackbar";
import { IconLoader2, IconUpload } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useMemo, useRef, useState } from "react";
import {
  ADMIN_DEFAULT_INVITER,
  BULK_IMPORT_MAX_VISIBLE_ROWS,
  BULK_IMPORT_ROW_HEIGHT,
} from "@/components/admin.users.constants";
import { useAdminAllUsers } from "@/lib/admin/directory.hooks";
import {
  downloadAdminUsersCsvTemplate,
  type ParsedCsvRow,
  parseAdminUsersCsv,
  useAdminBulkCreateUsers,
  useAdminEmailsExist,
} from "@/lib/admin/users.hooks";
import { graphqlErrorMessage } from "@/lib/graphql/client";

// Shared column layout for both preview and results lists
// #  Email  Name  Company  Job title  Credits  Invite  Invited by  HubSpot ID  Org ID / Error
const COLS = "grid-cols-[2.5rem_2fr_1.5fr_1.5fr_1fr_5rem_8rem_9rem_7rem_7rem]";

const ColHeaders = ({ children }: { children: React.ReactNode }) => (
  <div className={`grid ${COLS} bg-muted/40 border-b text-xs font-medium text-muted-foreground px-3 py-2`}>
    {children}
  </div>
);

function PreviewList({
  rows,
  duplicateRows,
  existsInSystemRows,
  invalidEmailRows,
  userNameByEmail,
  usersLoaded,
}: {
  rows: ParsedCsvRow[];
  duplicateRows: Set<number>;
  existsInSystemRows: Set<number>;
  invalidEmailRows: Set<number>;
  userNameByEmail: Map<string, string>;
  usersLoaded: boolean;
}) {
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => BULK_IMPORT_ROW_HEIGHT,
    overscan: 8,
  });

  const containerHeight = Math.min(rows.length, BULK_IMPORT_MAX_VISIBLE_ROWS) * BULK_IMPORT_ROW_HEIGHT;

  return (
    <div className="rounded border overflow-hidden">
      <ColHeaders>
        <span>#</span>
        <span>Email</span>
        <span>Name</span>
        <span>Company</span>
        <span>Job title</span>
        <span>Credits</span>
        <span>Invite</span>
        <span>Invited by</span>
        <span>HubSpot ID</span>
        <span>Org ID</span>
      </ColHeaders>
      <div ref={parentRef} style={{ height: containerHeight }} className="overflow-y-auto">
        <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
          {virtualizer.getVirtualItems().map((item) => {
            const row = rows[item.index];
            const isDuplicate = duplicateRows.has(row.row_number);
            const isExisting = existsInSystemRows.has(row.row_number);
            const isInvalidEmail = invalidEmailRows.has(row.row_number);
            const hasError = isDuplicate || isExisting || isInvalidEmail;
            const inviteLabel =
              row.invite_method === "PASSWORD_RESET" ? "Password reset" : row.invite ? "Signup link" : "—";
            const inviterEmail = (row.invited_by_email ?? ADMIN_DEFAULT_INVITER).toLowerCase();
            const inviterLabel = userNameByEmail.get(inviterEmail) ?? inviterEmail;
            const inviterUnknown =
              usersLoaded && row.invite && row.invite_method === "SIGNUP_INVITE" && !userNameByEmail.has(inviterEmail);
            const rowCls = `grid ${COLS} items-center px-3 text-sm border-b last:border-0 ${hasError ? "bg-destructive/10 text-destructive" : ""}`;
            const mutedCls = hasError ? "" : "text-muted-foreground";
            return (
              <div
                key={item.key}
                style={{ position: "absolute", top: item.start, left: 0, right: 0, height: BULK_IMPORT_ROW_HEIGHT }}
                className={rowCls}
              >
                <span className={mutedCls}>{row.row_number}</span>
                <span className="truncate pr-2">{row.email}</span>
                <span className={`truncate pr-2 ${mutedCls}`}>
                  {[row.first_name, row.last_name].filter(Boolean).join(" ") || "—"}
                </span>
                <span className={`truncate pr-2 ${mutedCls}`}>{row.company || "—"}</span>
                <span className={`truncate pr-2 ${mutedCls}`}>{row.jobtitle || "—"}</span>
                <span className={`truncate pr-2 ${mutedCls}`}>
                  {row.credits_usd != null ? `$${row.credits_usd}` : "—"}
                </span>
                <span className={mutedCls}>
                  {isInvalidEmail ? "Invalid email" : isExisting ? "Already exists" : inviteLabel}
                </span>
                <span
                  className={`truncate pr-2 ${inviterUnknown ? "text-yellow-600 dark:text-yellow-400" : mutedCls}`}
                  title={
                    inviterUnknown
                      ? "This email is not recognised as a user in the system. Invitations may fail."
                      : undefined
                  }
                >
                  {row.invite ? inviterLabel : "—"}
                </span>
                <span className={`truncate pr-2 font-mono text-xs ${mutedCls}`}>{row.hubspot_contact_id || "—"}</span>
                <span className={`truncate font-mono text-xs ${mutedCls}`}>{row.org_id || "—"}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

type ResultRow = { row_number: number; email: string; success: boolean; errors: string[] };

function ResultList({
  rows,
  previewRows,
  userNameByEmail,
}: {
  rows: ResultRow[];
  previewRows: ParsedCsvRow[];
  userNameByEmail: Map<string, string>;
}) {
  const parentRef = useRef<HTMLDivElement>(null);
  const previewMap = useMemo(() => new Map(previewRows.map((r) => [r.row_number, r])), [previewRows]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => BULK_IMPORT_ROW_HEIGHT,
    overscan: 8,
  });

  const containerHeight = Math.min(rows.length, BULK_IMPORT_MAX_VISIBLE_ROWS) * BULK_IMPORT_ROW_HEIGHT;

  return (
    <div className="rounded border overflow-hidden">
      <ColHeaders>
        <span>#</span>
        <span>Email</span>
        <span>Name</span>
        <span>Company</span>
        <span>Job title</span>
        <span>Credits</span>
        <span>Status</span>
        <span>Invited by</span>
        <span>HubSpot ID</span>
        <span>Error</span>
      </ColHeaders>
      <div ref={parentRef} style={{ height: containerHeight }} className="overflow-y-auto">
        <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
          {virtualizer.getVirtualItems().map((item) => {
            const row = rows[item.index];
            const src = previewMap.get(row.row_number);
            const alreadyExists = row.errors.some((e) => e.toLowerCase().includes("already exists"));
            const inviterEmail = (src?.invited_by_email ?? ADMIN_DEFAULT_INVITER).toLowerCase();
            const inviterLabel = userNameByEmail.get(inviterEmail) ?? inviterEmail;
            return (
              <div
                key={item.key}
                style={{ position: "absolute", top: item.start, left: 0, right: 0, height: BULK_IMPORT_ROW_HEIGHT }}
                className={`grid ${COLS} items-center px-3 text-sm border-b last:border-0`}
              >
                <span className="text-muted-foreground">{row.row_number}</span>
                <span className="truncate pr-2">{row.email}</span>
                <span className="truncate pr-2 text-muted-foreground">
                  {[src?.first_name, src?.last_name].filter(Boolean).join(" ") || "—"}
                </span>
                <span className="truncate pr-2 text-muted-foreground">{src?.company || "—"}</span>
                <span className="truncate pr-2 text-muted-foreground">{src?.jobtitle || "—"}</span>
                <span className="truncate pr-2 text-muted-foreground">
                  {src?.credits_usd != null ? `$${src.credits_usd}` : "—"}
                </span>
                <span>
                  {row.success ? (
                    <span className="text-green-600">Imported</span>
                  ) : alreadyExists ? (
                    <span className="text-muted-foreground">Exists</span>
                  ) : (
                    <span className="text-destructive">Failed</span>
                  )}
                </span>
                <span className="truncate pr-2 text-muted-foreground">{src?.invite ? inviterLabel : "—"}</span>
                <span className="truncate pr-2 font-mono text-xs text-muted-foreground">
                  {src?.hubspot_contact_id || "—"}
                </span>
                <span
                  className="truncate text-muted-foreground text-xs"
                  title={!row.success && !alreadyExists ? row.errors.join("; ") : undefined}
                >
                  {!row.success && !alreadyExists ? row.errors.join("; ") : ""}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

interface AdminUsersBulkImportDialogProps {
  open: boolean;
  onClose: () => void;
}

export const AdminUsersBulkImportDialog = ({ open, onClose }: AdminUsersBulkImportDialogProps) => {
  const queryClient = useQueryClient();
  const bulkCreate = useAdminBulkCreateUsers();
  const fileRef = useRef<HTMLInputElement>(null);

  // Used to resolve invited_by emails to display names in the preview
  const allUsersQuery = useAdminAllUsers(false, open);
  const userNameByEmail = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of allUsersQuery.data?.adminAllUsers ?? []) {
      map.set(u.email.toLowerCase(), u.name || u.email);
    }
    return map;
  }, [allUsersQuery.data]);

  const [previewRows, setPreviewRows] = useState<ParsedCsvRow[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [resultRows, setResultRows] = useState<Array<{
    row_number: number;
    email: string;
    success: boolean;
    errors: string[];
  }> | null>(null);

  // Basic format check — catches missing domain, missing @, etc. before any network call.
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const invalidEmailRows = useMemo(
    () => new Set(previewRows.filter((r) => !EMAIL_RE.test(r.email)).map((r) => r.row_number)),
    [previewRows],
  );

  // Check parsed emails against the database (single round-trip for all rows).
  const allEmails = useMemo(() => previewRows.map((r) => r.email), [previewRows]);
  const emailsExistQuery = useAdminEmailsExist(allEmails, previewRows.length > 0);
  const existingEmailsInSystem = useMemo(
    () => new Set((emailsExistQuery.data?.adminEmailsExist ?? []).map((e) => e.toLowerCase())),
    [emailsExistQuery.data],
  );

  const existsInSystemRows = useMemo(
    () =>
      new Set(previewRows.filter((r) => existingEmailsInSystem.has(r.email.toLowerCase())).map((r) => r.row_number)),
    [previewRows, existingEmailsInSystem],
  );

  // Detect duplicate emails and hubspot_contact_ids within the CSV itself.
  const { duplicateRows, duplicateMessages } = useMemo(() => {
    const emailCounts = new Map<string, number[]>();
    const hubspotCounts = new Map<string, number[]>();

    for (const row of previewRows) {
      const email = row.email.toLowerCase();
      emailCounts.set(email, [...(emailCounts.get(email) ?? []), row.row_number]);

      if (row.hubspot_contact_id) {
        const hs = row.hubspot_contact_id.toLowerCase();
        hubspotCounts.set(hs, [...(hubspotCounts.get(hs) ?? []), row.row_number]);
      }
    }

    const dupeRowNums = new Set<number>();
    const messages: string[] = [];

    for (const [email, rowNums] of emailCounts) {
      if (rowNums.length > 1) {
        for (const n of rowNums) dupeRowNums.add(n);
        messages.push(`Duplicate email "${email}" in rows ${rowNums.join(", ")}.`);
      }
    }
    for (const [hs, rowNums] of hubspotCounts) {
      if (rowNums.length > 1) {
        for (const n of rowNums) dupeRowNums.add(n);
        messages.push(`Duplicate HubSpot contact ID "${hs}" in rows ${rowNums.join(", ")}.`);
      }
    }

    return { duplicateRows: dupeRowNums, duplicateMessages: messages };
  }, [previewRows]);

  // Rows that are safe to submit — excludes invalid emails, in-CSV duplicates, and already-existing users.
  const goodRows = useMemo(
    () =>
      previewRows.filter(
        (r) =>
          !invalidEmailRows.has(r.row_number) &&
          !duplicateRows.has(r.row_number) &&
          !existsInSystemRows.has(r.row_number),
      ),
    [previewRows, invalidEmailRows, duplicateRows, existsInSystemRows],
  );

  const reset = () => {
    setPreviewRows([]);
    setParseErrors([]);
    setResultRows(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? "");
      const { rows, errors } = parseAdminUsersCsv(text);
      setPreviewRows(rows);
      setParseErrors(errors);
      setResultRows(null);
    };
    reader.readAsText(file);
  };

  const BULK_IMPORT_MAX_ROWS = 500;

  const handleImport = () => {
    if (goodRows.length === 0) {
      toast.error("No valid rows to import.");
      return;
    }

    if (goodRows.length > BULK_IMPORT_MAX_ROWS) {
      toast.error(`Import exceeds the ${BULK_IMPORT_MAX_ROWS}-row limit. Please split your file and try again.`);
      return;
    }

    bulkCreate.mutate(
      {
        rows: goodRows,
      },
      {
        onSuccess: (data) => {
          const result = data.adminBulkCreateUsers;
          setResultRows(result.rows);
          toast.success(`Imported ${result.created_count} user(s), ${result.failed_count} failed.`);
          void queryClient.invalidateQueries({ queryKey: ["adminAllUsers"] });
          void queryClient.invalidateQueries({ queryKey: ["adminEmailsExist"] });
        },
        onError: (error) => toast.error(graphqlErrorMessage(error, "Bulk import failed.")),
      },
    );
  };

  const downloadErrorReport = () => {
    if (!resultRows) return;
    const header = "row_number,email,success,errors\n";
    const body = resultRows.map((r) => `${r.row_number},${r.email},${r.success},"${r.errors.join("; ")}"`).join("\n");
    const blob = new Blob([header + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "admin-users-import-results.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-6xl">
        <DialogHeader>
          <DialogTitle>Import users from CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV file using the{" "}
            <button type="button" className="underline" onClick={downloadAdminUsersCsvTemplate}>
              template
            </button>
            . Up to 500 rows per import.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
            }}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              if (fileRef.current) {
                fileRef.current.value = "";
                fileRef.current.click();
              }
            }}
          >
            <IconUpload className="mr-1 size-4" />
            Choose CSV file
          </Button>

          {parseErrors.length > 0 && (
            <ul className="text-sm text-amber-600">
              {parseErrors.map((err) => (
                <li key={err}>{err}</li>
              ))}
            </ul>
          )}

          {!resultRows &&
            (invalidEmailRows.size > 0 || duplicateMessages.length > 0 || existsInSystemRows.size > 0) && (
              <ul className="text-sm text-amber-600">
                {invalidEmailRows.size > 0 && (
                  <li>
                    {invalidEmailRows.size} row{invalidEmailRows.size > 1 ? "s have" : " has"} an invalid email address
                    and will be skipped (rows{" "}
                    {Array.from(invalidEmailRows)
                      .sort((a, b) => a - b)
                      .join(", ")}
                    ).
                  </li>
                )}
                {duplicateMessages.map((msg) => (
                  <li key={msg}>{msg.replace(/\.$/, "")} — duplicates will be skipped.</li>
                ))}
                {existsInSystemRows.size > 0 && (
                  <li>
                    {existsInSystemRows.size} row{existsInSystemRows.size > 1 ? "s" : ""} already exist
                    {existsInSystemRows.size > 1 ? "" : "s"} in the system and will be skipped (rows{" "}
                    {Array.from(existsInSystemRows)
                      .sort((a, b) => a - b)
                      .join(", ")}
                    ).
                  </li>
                )}
              </ul>
            )}

          {previewRows.length > 0 && !resultRows && (
            <PreviewList
              rows={previewRows}
              duplicateRows={duplicateRows}
              existsInSystemRows={existsInSystemRows}
              invalidEmailRows={invalidEmailRows}
              userNameByEmail={userNameByEmail}
              usersLoaded={allUsersQuery.isSuccess}
            />
          )}

          {resultRows && (
            <div className="flex flex-col gap-2">
              <ResultList rows={resultRows} previewRows={previewRows} userNameByEmail={userNameByEmail} />
              <Button type="button" variant="outline" size="sm" onClick={downloadErrorReport}>
                Download results CSV
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            Close
          </Button>
          {!resultRows && (
            <Button type="button" disabled={goodRows.length === 0 || bulkCreate.isPending} onClick={handleImport}>
              {bulkCreate.isPending ? (
                <>
                  <IconLoader2 className="mr-1 size-4 animate-spin" />
                  Importing…
                </>
              ) : (
                `Import ${goodRows.length} user(s)`
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
